import React, {
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Form, FormField, FormGroup, Icon, Input, Label, Modal, Popup } from "semantic-ui-react";

import moment from "moment";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { SelectedItemHandler } from "react-lib/appcon/common/SearchBoxDropdown";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";

import TimeComboBoxStatic from "../common/TimeComboBoxStatic";

import {
  AddProviderParamsType,
  BTN_ACTS,
  getProviderProfile,
  GroupedScheduleEventType,
  ScheduleEventType,
  timeToDecimal,
} from "./sequence/ScheduleSetting";

type ModAddProviderProps = {
  onEvent: (e: any) => void;
  setProp: (key: any, value: any, callback?: () => void) => any;
  divisionId: number;
  events: GroupedScheduleEventType[];
  open: boolean;
  selectedEventIds: number[];
  selectedWeekRange: { endWeek: string; startWeek: string };
  buttonLoadCheck: any;
  searchedItemListWithKey: any;
  onClose: () => void;
  onPastDate: () => void;
  onSave: (data: AddProviderParamsType) => void;
};

type Styles = Record<
  | "bullet"
  | "circularBadge"
  | "modal"
  | "providerAvatar"
  | "providerItem"
  | "saveButton"
  | "scheduleGrid"
  | "totalBadge",
  CSSProperties
>;

const styles: Styles = {
  bullet: {
    backgroundColor: "rgba(0,0,0,.87)",
    borderRadius: "500rem",
    height: "4px",
    marginTop: "1.375rem",
    width: "4px",
  },
  circularBadge: {
    alignItems: "center",
    backgroundColor: "#ffeff3",
    borderRadius: "500rem",
    display: "flex",
    fontSize: "0.75rem",
    fontWeight: "bold",
    height: "27.5px",
    justifyContent: "center",
    marginRight: "0.25rem",
    paddingTop: "0.1rem",
    width: "27.5px",
  },
  modal: {
    border: "1px solid var(--primary-theme-color)",
    minWidth: "35rem",
    padding: "2rem 1.5rem",
    width: "fit-content",
  },
  providerAvatar: {
    alignItems: "center",
    border: "1px solid rgba(179, 179, 179, 1)",
    borderRadius: "50%",
    display: "flex",
    height: "35px",
    justifyContent: "center",
    marginRight: "1rem",
    width: "35px",
  },
  providerItem: {
    alignItems: "center",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    marginBottom: "0.25rem",
    padding: "0.25rem 0.5rem",
    position: "relative",
    transition: "background-color 0.2s ease",
    width: "85%",
  },
  saveButton: {
    background: "var(--primary-theme-color)",
    border: "none",
    borderRadius: "6px",
    color: "white",
  },
  scheduleGrid: {
    display: "grid",
    gridTemplateColumns: "auto max-content 1fr",
    marginLeft: "0.95rem",
    rowGap: "1rem",
  },
  totalBadge: {
    backgroundColor: "rgba(255, 212, 223, 1)",
    border: "1px solid var(--Select-box, rgba(255, 212, 223, 1))",
    borderRadius: "1px",
    padding: "3px 16px",
  },
};

const IMAGES = {
  provider: "/static/images/schedule/provider-icon.png",
};

const groupByDate = (data: GroupedScheduleEventType[]) => {
  const grouped = new Map<string, GroupedScheduleEventType>();

  for (const slot of data) {
    const dateKey = slot.date;

    if (grouped.has(dateKey)) {
      grouped.get(dateKey)?.items.push(...slot.items);
    } else {
      grouped.set(dateKey, {
        ...slot,
        items: [...slot.items],
        time_slots: [],
      });
    }
  }

  return [...grouped.values()].map((group) => {
    // สร้าง time_slots พร้อม items
    const timeSlotMap = new Map<
      string,
      {
        end_time: string;
        items: ScheduleEventType[];
        start_time: string;
      }
    >();

    for (const item of group.items) {
      const start = moment(item.start_datetime).format("HH:mm");
      const end = moment(item.end_datetime).format("HH:mm");
      const key = `${start}-${end}`;

      if (!timeSlotMap.has(key)) {
        timeSlotMap.set(key, {
          end_time: end,
          items: [],
          start_time: start,
        });
      }

      timeSlotMap.get(key)?.items.push(item);
    }

    const timeSlots = [...timeSlotMap.values()].sort((a, b) =>
      a.start_time.localeCompare(b.start_time)
    );

    return {
      ...group,
      items: [...group.items].sort(
        (a, b) => moment(a.start_datetime).valueOf() - moment(b.start_datetime).valueOf()
      ),
      time_slots: timeSlots,
    };
  });
};

const ModAddProvider = (props: ModAddProviderProps) => {
  const [selectedProviders, setSelectedProviders] = useState<Record<string, any>[]>([]);
  const [slotLength, setSlotLength] = useState<string>("");
  const [isErrMsgDuration, setIsErrMsgDuration] = useState<boolean>(false);
  const [timeSlots, setTimeSlots] = useState<
    Partial<Record<string, { end_time?: string; start_time?: string }>>
  >({});

  useEffect(() => {
    if (!props.open) {
      return;
    }

    setSelectedProviders([]);
    setSlotLength("");
    setIsErrMsgDuration(false);
    setTimeSlots({});
  }, [props.open]);

  const filteredEvents = useMemo(
    () => props.events.filter((event) => props.selectedEventIds.includes(event.id)),
    [props.events, props.selectedEventIds]
  );

  const formattedEvents = useMemo(() => groupByDate(filteredEvents), [filteredEvents]);

  const updatedEventItems = useMemo(() => {
    const updateItemWithTimeSlot = (
      item: ScheduleEventType,
      eventIndex: number,
      slotIndex: number
    ) => {
      const originalSlot = formattedEvents[eventIndex].time_slots[slotIndex];
      const modifiedSlot = timeSlots[`${eventIndex}-${slotIndex}`];

      const startTime = modifiedSlot?.start_time || originalSlot.start_time;
      const endTime = modifiedSlot?.end_time || originalSlot.end_time;
      const startDate = moment(item.start_datetime).format("YYYY-MM-DD");

      return {
        ...item,
        end_datetime: `${startDate} ${endTime}`,
        start_datetime: `${startDate} ${startTime}`,
      };
    };

    return formattedEvents.flatMap((event, eventIndex) =>
      event.time_slots.flatMap((slot, slotIndex) =>
        slot.items.map((item) => updateItemWithTimeSlot(item, eventIndex, slotIndex))
      )
    );
  }, [formattedEvents, timeSlots]);

  const mapOptions = useCallback(
    (items: any[]) =>
      items.filter(Boolean).map((item) => ({
        key: item.provider_id,
        text: `${item.pre_name || ""} ${item.first_name || ""} ${item.last_name || ""}`,
        value: item.provider_id,
      })),
    []
  );

  const handleSelect: SelectedItemHandler = (value, key, data) => {
    if (data) {
      const foundProvider = selectedProviders.find((item) => item.provider_id === data.provider_id);

      if (!foundProvider) {
        setSelectedProviders([...selectedProviders, data]);

        props.onEvent({ message: "ItemSearch", params: { action: "clear", id: "MAP" } });
      }
    }
  };

  const handleRemoveProvider = (indexToRemove: number) => {
    setSelectedProviders(selectedProviders.filter((_, index) => index !== indexToRemove));
  };

  const handleSave = () => {
    if (!Number.isNaN(slotLength)) {
      const isInvalidDuration = Number(slotLength) % 5 !== 0;

      if (!slotLength || isInvalidDuration) {
        setIsErrMsgDuration(true);

        return;
      }
    }

    setIsErrMsgDuration(false);

    const hasPastDate = updatedEventItems.some((item) =>
      moment(item.start_datetime).isBefore(moment())
    );

    if (hasPastDate) {
      props.onPastDate();

      return;
    }

    props.onSave({
      events: updatedEventItems,
      providers: selectedProviders,
      slot_length: Number(slotLength),
    });
  };

  const handleMouseEnter = (e: MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.05)";
    const closeIcon = e.currentTarget.querySelector(".close-icon") as HTMLElement | null;

    if (closeIcon) {
      closeIcon.style.display = "flex";
    }
  };

  const handleMouseLeave = (e: MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.backgroundColor = "transparent";
    const closeIcon = e.currentTarget.querySelector(".close-icon") as HTMLElement | null;

    if (closeIcon) {
      closeIcon.style.display = "none";
    }
  };

  const handleChangeTimeSlot =
    (key: string, field: "end_time" | "start_time") => (time: string) => {
      const [pIndex, cIndex] = key.split("-");
      const timeSlot = formattedEvents[Number(pIndex)].time_slots[Number(cIndex)];

      const selectedTime = moment(time, "HH:mm");
      const startTime = moment(timeSlot.start_time, "HH:mm");
      const endTime = moment(timeSlot.end_time, "HH:mm");

      // ตรวจสอบว่าเวลาที่เลือกอยู่ในช่วงหรือไม่
      if (selectedTime.isBetween(startTime, endTime, null, "[]")) {
        setTimeSlots((prev) => ({ ...prev, [key]: { ...prev[key], [field]: time } }));
      } else {
        // ถ้าอยู่นอกช่วง ให้ใช้ค่าจาก timeSlot
        setTimeSlots({
          ...timeSlots,
          [key]: { end_time: timeSlot.end_time, start_time: timeSlot.start_time },
        });
      }
    };

  return (
    <Modal style={styles.modal} open={props.open} closeOnDimmerClick onClose={props.onClose}>
      <div style={{ position: "absolute", right: "0.5rem", top: "1rem" }}>
        <Icon style={{ fontSize: "1.15rem" }} name="close" link onClick={props.onClose} />
      </div>
      <div style={{ alignItems: "center", display: "flex", margin: "0.75rem 0 1.55rem" }}>
        <div style={{ fontSize: "16px", fontWeight: "bold", marginRight: "1rem" }}>
          Add Provider to Schedule
        </div>
        <Label style={{ backgroundColor: "#ffeff3" }} size="large">
          {props.selectedWeekRange.startWeek}
        </Label>
        {!!props.selectedWeekRange.endWeek && (
          <>
            <label style={{ fontWeight: "bold", margin: "0 0.5rem" }}>To</label>
            <Label style={{ backgroundColor: "#ffeff3" }} size="large">
              {props.selectedWeekRange.endWeek}
            </Label>
          </>
        )}
      </div>
      <div style={styles.scheduleGrid}>
        {formattedEvents.map((item, pIndex) => {
          const [dayOfWeek, ...restOfDate] = item.full_date_limited.split(" ");
          const formattedDates = restOfDate.join(" ");

          return (
            <React.Fragment key={`slot-${pIndex}`}>
              <div style={styles.bullet} />
              <label style={{ marginTop: "0.45rem", paddingLeft: "0.5rem", paddingRight: "3rem" }}>
                <Popup
                  style={{ border: "1px solid var(--primary-theme-color)", minWidth: "26.5rem" }}
                  content={item.full_date_first_month_only}
                  disabled={!item.full_date_limited.includes("...")}
                  position="bottom left"
                  trigger={
                    <label style={{ alignItems: "center", display: "flex" }}>
                      <label style={styles.circularBadge}>{dayOfWeek}</label>
                      <label>{formattedDates}</label>
                    </label>
                  }
                />
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", rowGap: "0.25rem" }}>
                {item.time_slots.map((slot, cIndex) => {
                  const key = `${pIndex}-${cIndex}`;
                  const startTime = timeSlots[key]?.start_time || slot.start_time;
                  const endTime = timeSlots[key]?.end_time || slot.end_time;

                  return (
                    <div
                      key={key}
                      style={{
                        alignItems: "center",
                        display: "flex",
                        marginRight: "1rem",
                      }}
                    >
                      <TimeComboBoxStatic
                        style={{ width: "6.75rem" }}
                        interval={15}
                        maxHour={timeToDecimal(slot.end_time)}
                        minHour={timeToDecimal(slot.start_time)}
                        value={startTime}
                        onTextChange={handleChangeTimeSlot(key, "start_time")}
                      />
                      <label style={{ padding: "0 0.5rem" }}>-</label>
                      <TimeComboBoxStatic
                        style={{ width: "6.75rem" }}
                        interval={15}
                        maxHour={timeToDecimal(slot.end_time)}
                        minHour={timeToDecimal(slot.start_time)}
                        value={endTime}
                        onTextChange={handleChangeTimeSlot(key, "end_time")}
                      />
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <Form>
        <FormGroup style={{ marginTop: "1.75rem", position: "relative" }} inline>
          <FormField width={10} inline required>
            <label style={{ minWidth: "max-content" }}>Service time/Slot</label>
            <Input
              type="number"
              value={slotLength}
              fluid
              onChange={(e, data) => {
                setSlotLength(data.value);
              }}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                if (/^[e\-+.]$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </FormField>
          <FormField>
            <label>min</label>
          </FormField>
        </FormGroup>
        {isErrMsgDuration && (
          <FormGroup style={{ marginTop: "-0.5rem" }} inline>
            <FormField width={13} inline>
              <div
                style={{
                  color: "red",
                  fontSize: "0.825rem",
                  marginLeft: "auto",
                }}
              >
                * กรุณาระบุนาทีที่หารด้วย 5 ลงตัว เช่น 5, 10, 15
              </div>
            </FormField>
          </FormGroup>
        )}
        <FormGroup style={{ marginTop: "1.5rem" }} inline>
          <FormField width={14} inline>
            <img
              style={{ height: "17px", marginRight: "1rem", width: "auto" }}
              alt="provider.icon"
              src={IMAGES.provider}
            />
            <SearchBoxWithKey
              onEvent={props.onEvent}
              id="MAP"
              placeholder="Search Provider"
              type="Provider"
              valueKey="provider_id"
              searchedItemListWithKey={props.searchedItemListWithKey}
              mapOptions={mapOptions}
              setSelected={handleSelect}
              onAdditionalUrlParams={() => ({ division: props.divisionId })}
            />
          </FormField>
        </FormGroup>
      </Form>
      <div style={{ marginTop: "1.25rem" }}>
        {selectedProviders.length > 0 && (
          <>
            <div>
              <label style={{ paddingRight: "1.25rem" }}>Total: </label>
              <label style={styles.totalBadge}>{selectedProviders.length}</label>
            </div>
            <div style={{ margin: "1.25rem 0 0 1.5rem" }}>
              {selectedProviders.map((item, index) => {
                const provider = getProviderProfile(item);

                return (
                  <div
                    key={`provider${index}`}
                    style={styles.providerItem}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div
                      style={{
                        ...styles.providerAvatar,
                        backgroundColor: provider.color.light,
                      }}
                    >
                      {provider.abbr}
                    </div>
                    <label style={{ cursor: "pointer", flex: 1 }}>{provider.full_name}</label>
                    <div className="close-icon">
                      <Icon
                        style={{ color: "rgba(79, 79, 79, 1)" }}
                        name="close"
                        onClick={() => {
                          handleRemoveProvider(index);
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
          <ButtonLoadCheck
            setProp={props.setProp}
            style={styles.saveButton}
            color="green"
            disabled={selectedProviders.length === 0}
            paramKey={BTN_ACTS.ADD_PROVIDER}
            size="medium"
            title="Save"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.ADD_PROVIDER]}
            onClick={handleSave}
          />
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(ModAddProvider);
